/**
 * @Author: qinyifang
 * @Date: 2025-04-28 14:34:44
 * @Description: 路由配置文件，定义应用的路由结构和参数
 */
import { type RouteRecordRaw, createRouter, createWebHistory } from "vue-router"
import { useUserStore } from "@/store/user"
import { isAgenticDomain, stripQueryKeys, buildSobotLoginUrl } from "@/assets/js/utils/utils"

// 常驻路由 (ai.jlpay.com)
export const constantRoutes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/login.vue"),
    meta: {
      title: "嘉联AI+",
      login: false
    }
  },
  {
    path: "/home",
    name: "Home",
    component: () => import("@/views/MainView.vue"),
    meta: {
      title: "嘉联AI+",
      login: true
    }
  },
  {
    path: "/inline",
    name: "Inline",
    component: () => import("@/views/inline/index.vue"),
    meta: {
      title: "内联页面",
      login: true
    }
  },
  {
    path: "/common",
    name: "Common",
    component: () => import("@/views/product/common.vue"),
    meta: {
      title: "产品公共页面",
      login: false
    }
  },
  {
    path: "/agent",
    name: "Agent",
    component: () => import("@/views/product/agent.vue"),
    meta: {
      title: "AI嘉",
      login: false
    }
  },
  {
    path: "/agent-mobile",
    name: "AgentMobile",
    component: () => import("@/views/product/agent.vue"),
    meta: {
      title: "AI嘉",
      login: false
    }
  },
  {
    // 95坐席工作台 AI 辅助问答（简化版），供 React 平台 iframe 嵌入
    path: "/seat-assistant",
    name: "SeatAssistant",
    component: () => import("@/views/product/seatAssistant.vue"),
    meta: {
      title: "AI坐席辅助",
      login: false
    }
  },
  {
    // 智齿人工坐席服务小结（表单），供智齿客服工作台 iframe 嵌入
    // 需登录（企微扫码，token 存 ai 平台桶）：未登录由守卫跳 /login 并带 target 回跳
    path: "/sobot-summary",
    name: "SobotSummary",
    component: () => import("@/views/product/sobotSummary.vue"),
    meta: {
      title: "服务小结"
    }
  },
  {
    path: "/error",
    name: "Error",
    component: () => import("@/views/error/index.vue"),
    meta: {
      title: "错误页面",
      login: false
    }
  },
  {
    // 服务意见反馈 - 投诉填写页（支持 query 预填：content/category/work_source）
    path: "/complaint",
    name: "Complaint",
    component: () => import("@/views/complaint/index.vue"),
    meta: {
      title: "服务意见反馈",
      login: false
    }
  },
  {
    // 服务意见反馈 - 结果页（query.status: success/fail）
    path: "/complaint/result",
    name: "ComplaintResult",
    component: () => import("@/views/complaint/result.vue"),
    meta: {
      title: "服务意见反馈",
      login: false
    }
  },
  {
    path: "/",
    redirect: "/login"
  }
]

// Agentic 路由 (agentic.jlpay.com)
export const agenticRoutes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/login.vue"),
    meta: {
      title: "嘉联AI+",
      login: false
    }
  },
  {
    path: "/home",
    name: "AgenticHome",
    component: () => import("@/agentic/views/MainView.vue"),
    meta: {
      title: "嘉联AI+",
      login: true
    }
  },
  {
    path: "/inline",
    name: "Inline",
    component: () => import("@/views/inline/index.vue"),
    meta: {
      title: "内联页面",
      login: true
    }
  },
  {
    path: "/",
    redirect: "/login"
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/login"
  }
]

const base = import.meta.env.VITE_BASE_URL
export const router = createRouter({
  history: createWebHistory(base),
  routes: isAgenticDomain() ? agenticRoutes : constantRoutes
})

// 路由守卫：根据 meta.login 判断是否需要登录
router.beforeEach((to, from, next) => {
  // 设置页面标题
  if (typeof to.meta.title === "string") {
    document.title = to.meta.title
  }

  // 处理登录状态
  const userStore = useUserStore()
  const requiresLogin = to.meta.login !== false

  // --- 统筹处理平台识别与 Store 更新 ---
  let platform = ""
  if (isAgenticDomain()) {
    platform = "agentic"
  } else if (to.path === "/agent") {
    const qPlatform = to.query.platform as string
    platform = ["agent_posp", "agent_mpos"].includes(qPlatform) ? qPlatform : "agent_pc"
  } else if (to.path === "/agent-mobile") {
    const qPlatform = to.query.platform as string
    platform = ["agent_posp", "agent_mpos"].includes(qPlatform) ? qPlatform : "agent_app"
  } else if (to.path === "/seat-assistant") {
    // 坐席辅助页固定使用 manage 平台标识（父平台 basic-operation-module）
    platform = "manage"
  } else if (to.path === "/sobot-summary") {
    // 智齿人工坐席页复用 ai 平台标识（请求头 Platform: ai，token 走企微登录态存 ai 桶，与 /home 同源）
    platform = "ai"
  } else if (to.path === "/common" && to.query.platform) {
    platform = to.query.platform as string
  } else if (to.path.startsWith("/complaint") && to.query.platform) {
    // 投诉页从 AI 嘉对话跳转，需透传 platform 以正确读取对应平台登录态
    platform = to.query.platform as string
  } else if (["/home", "/inline", "/login"].includes(to.path) || to.path === "/") {
    platform = "ai"
  }

  // 只要识别到了平台，就更新到 Store
  if (platform && userStore.currentPlatform !== platform) {
    userStore.setCurrentPlatform(platform)
  }

  // 检查是否已登录（根据当前 Store 中的平台标识获取登录状态）
  // 特殊情况：如果是中转登录（去往 /login 且携带绝对 URL target），则强制不使用缓存 token，直接展示登录页；
  // 同应用路径 target（以 / 开头，如 /sobot-summary）不强制：目标平台已有登录态时由下方已登录分支直接回跳
  const rawTarget = typeof to.query.target === "string" ? to.query.target : ""
  const isTransferPage = to.path === "/login" && Boolean(rawTarget) && !rawTarget.startsWith("/")
  const loginInfo = isTransferPage ? null : userStore.getLoginInfoByPlatform()
  const isLoggedIn = loginInfo && loginInfo.token

  // 如果已登录
  if (isLoggedIn) {
    if (to.path === "/login") {
      // 登录页携带同应用 target（以 / 开头的站内路径）且目标平台已有登录态：
      // 保持登录态直接整页回跳目标页并透传其余 URL 参数（如 sobot-summary 免二次扫码）
      if (rawTarget.startsWith("/")) {
        window.location.replace(rawTarget + stripQueryKeys(window.location.search, ["target", "code", "state"]))
        next(false)
        return
      }
      // 已登录访问登录页，跳转到首页
      next({ path: "/home" })
      return
    }
    next()
    return
  }

  // 如果未登录
  // 如果路由不需要登录（meta.login === false），允许访问
  if (!requiresLogin) {
    next()
    return
  }

  // 需要登录但未登录，跳转到登录页
  if (to.path === "/sobot-summary") {
    // 智齿 iframe 页未登录：跳登录页并携带 target + 原始 URL 参数（字符串拼接保真，
    // 避免整段编码参数经 vue-router query 往返被二次编码），登录成功后由登录页回跳
    window.location.replace(buildSobotLoginUrl())
    next(false)
    return
  }
  next({ path: "/login" })
})
